/**
 * "Add server" (D21): pick how long the one-time code lives (and whether the
 * new server is the site's LAN witness, D9), generate it, and copy the
 * `docker run` line that installs a node with it. The code is shown once — the
 * cloud keeps only a hash — with its expiry. A replacement's code (after
 * "Replace node") arrives already generated, in the revoked node's role.
 */
import type { EnrolmentCode } from '@acp/api-contract/sites.js';
import { CopyIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

/** The code lifetimes offered (D21: at most 24 h). */
export const CODE_LIFETIMES: readonly { readonly minutes: number; readonly label: string }[] = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 8 * 60, label: '8 hours' },
  { minutes: 24 * 60, label: '24 hours' },
];

export type AddServerDialogProps = {
  readonly open: boolean;
  readonly siteName: string;
  /** The generated code, or null before one. */
  readonly enrolment: EnrolmentCode | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onGenerate: (request: { readonly minutes: number; readonly witness: boolean }) => void;
  readonly onCopy: (text: string) => void;
  readonly onOpenChange: (open: boolean) => void;
};

function Generated({ enrolment, onCopy, onOpenChange }: { readonly enrolment: EnrolmentCode; readonly onCopy: (text: string) => void; readonly onOpenChange: (open: boolean) => void }) {
  const role = enrolment.role === null ? 'the pairing order (first main, then backup)' : enrolment.role;
  return (
    <div className="grid gap-4">
      <DialogHeader>
        <DialogTitle>{enrolment.replacesNodeId === null ? 'Install the server' : 'Install the replacement'}</DialogTitle>
        <DialogDescription>
          Run this on the new host (Linux, Docker Engine, host networking). It works once, until {enrolment.expiresAt.slice(0, 16).replace('T', ' ')} UTC; the node takes {role}.
        </DialogDescription>
      </DialogHeader>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted p-3 font-mono text-xs" data-testid="docker-run">
        {enrolment.dockerRun}
      </pre>
      <p className="text-xs text-muted-foreground">This is the only time the code is shown: the cloud keeps just a hash of it.</p>
      <DialogFooter>
        <Button variant="outline" onClick={() => onCopy(enrolment.dockerRun)} data-testid="copy-docker-run">
          <CopyIcon />
          Copy command
        </Button>
        <Button onClick={() => onOpenChange(false)}>Done</Button>
      </DialogFooter>
    </div>
  );
}

function Options({ siteName, pending, error, onGenerate, onOpenChange }: Omit<AddServerDialogProps, 'open' | 'enrolment' | 'onCopy'>) {
  const [minutes, setMinutes] = useState(24 * 60);
  const [witness, setWitness] = useState(false);
  return (
    <div className="grid gap-4">
      <DialogHeader>
        <DialogTitle>Add a server to {siteName}</DialogTitle>
        <DialogDescription>The first server becomes main, the second backup. A third can only join as the site’s LAN witness, which lets the pair fail over in about a second.</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor="code-lifetime">The code works for</Label>
        <NativeSelect id="code-lifetime" value={String(minutes)} onChange={(e) => setMinutes(Number(e.target.value))}>
          {CODE_LIFETIMES.map((l) => (
            <option key={l.minutes} value={l.minutes}>
              {l.label}
            </option>
          ))}
        </NativeSelect>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={witness} onChange={(e) => setWitness(e.target.checked)} data-testid="witness-code" />
        This server is the site’s LAN witness
      </label>
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={() => onGenerate({ minutes, witness })} disabled={pending} data-testid="generate-code">
          {pending ? 'Generating…' : 'Generate install command'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export function AddServerDialog(props: AddServerDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        {!props.open ? null : props.enrolment === null ? (
          <Options siteName={props.siteName} pending={props.pending} error={props.error} onGenerate={props.onGenerate} onOpenChange={props.onOpenChange} />
        ) : (
          <Generated enrolment={props.enrolment} onCopy={props.onCopy} onOpenChange={props.onOpenChange} />
        )}
      </DialogContent>
    </Dialog>
  );
}
